import { cellToString } from "./cell";
import { resolveTemplate } from "@/modules/_shared/templateValue";
import type { Condition, Predicate, Templatable, ShopifyRow, SourceRow } from "./types";

/**
 * The comparison value of a predicate. A structured value is resolved against
 * the row/output (field refs become live values); a legacy STRING is used
 * literally, preserving exact prior behaviour for un-migrated configs.
 */
function predicateValue(
  v: Templatable | undefined,
  row: SourceRow,
  output?: ShopifyRow,
): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return resolveTemplate(v, { row, output });
}

export function readOperand(
  column: string,
  row: SourceRow,
  output?: ShopifyRow,
): string {
  const c = column.trim();
  if (c.startsWith("out:")) return output?.[c.slice(4).trim()] ?? "";
  return cellToString(row[c]);
}

function parseNumeric(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;

  const direct = Number(trimmed);
  if (Number.isFinite(direct)) return direct;

  let cleaned = "";
  let sawDot = false;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch >= "0" && ch <= "9") {
      cleaned += ch;
    } else if (ch === "." && !sawDot) {
      cleaned += ch;
      sawDot = true;
    } else if (ch === "-" && cleaned === "") {
      cleaned += ch;
    }
  }

  if (!/[0-9]/.test(cleaned)) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

export function evalPredicate(
  p: Predicate,
  row: SourceRow,
  output?: ShopifyRow,
): boolean {
  const raw = readOperand(p.column, row, output);
  const expected = predicateValue(p.value, row, output);
  const caseSensitive = p.caseSensitive === true;
  const left = caseSensitive ? raw : raw.toLowerCase();
  const right = caseSensitive ? expected : expected.toLowerCase();

  switch (p.op) {
    case "equals":
      return left === right;
    case "notEquals":
      return left !== right;
    // Substring/affix ops are meaningless against an empty expected value: every
    // string "contains"/"starts with"/"ends with" "", which would make the
    // predicate fire on every row (e.g. an unresolved field ref → ""). Treat an
    // empty expected value as "no match" so the branch is simply skipped.
    case "contains":
      return right === "" ? false : left.includes(right);
    case "notContains":
      return right === "" ? false : !left.includes(right);
    case "startsWith":
      return right === "" ? false : left.startsWith(right);
    case "endsWith":
      return right === "" ? false : left.endsWith(right);
    case "isEmpty":
      return raw.trim() === "";
    case "isNotEmpty":
      return raw.trim() !== "";
    case "matches": {
      const src = expected;
      if (src.length === 0 || src.length > 200) return false;
      try {
        return new RegExp(src, caseSensitive ? "" : "i").test(raw);
      } catch {
        return false;
      }
    }
    case "gt": {
      const lhs = parseNumeric(raw);
      const rhs = parseNumeric(expected);
      return lhs !== null && rhs !== null && lhs > rhs;
    }
    case "lt": {
      const lhs = parseNumeric(raw);
      const rhs = parseNumeric(expected);
      return lhs !== null && rhs !== null && lhs < rhs;
    }
    default:
      return false;
  }
}

export function evalCondition(
  cond: Condition,
  row: SourceRow,
  output?: ShopifyRow,
): boolean {
  const predicates = "all" in cond ? cond.all : cond.any;
  if (predicates.length === 0) return false;

  // New shape: at least one predicate carries an explicit per-condition joiner.
  // Evaluate as a sequential left-fold, applying each predicate's own join
  // against the running result. The first predicate's join is ignored.
  const hasExplicitJoin = predicates.some((p) => p.join === "and" || p.join === "or");
  if (hasExplicitJoin) {
    let acc = evalPredicate(predicates[0], row, output);
    for (let i = 1; i < predicates.length; i++) {
      const next = evalPredicate(predicates[i], row, output);
      acc = predicates[i].join === "or" ? acc || next : acc && next;
    }
    return acc;
  }

  // Legacy shape: group-level combinator. all → every, any → some.
  if ("all" in cond) {
    return cond.all.every((p) => evalPredicate(p, row, output));
  }
  return cond.any.some((p) => evalPredicate(p, row, output));
}
